function getCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export function chartBackground() {
  return getCssVar('--ds-surface-base');
}

/**
 * DS ALPHA Chart Palette — Categorical/Default
 */
const CHART_PALETTE = [
  '#3B82F6', // Blue/60
  '#14B8A6', // Teal/65
  '#F97316', // Orange/70
  '#FACC15', // Yellow/80
  '#A855F7', // Purple/60
  '#EC4899', // Pink/60
  '#34D399', // Emerald/65
  '#6366F1', // Indigo/60
  '#22D3EE', // Cyan/60
];

/**
 * DS ALPHA Chart Palette — Categorical/High Contrast (lighter)
 */
export const CHART_PALETTE_LIGHT = [
  '#60A5FA', // Blue/40
  '#5EEAD4', // Teal/40
  '#FDBA74', // Orange/40
  '#FDE68A', // Yellow/65
  '#C084FC', // Purple/40
  '#F9A8D4', // Pink/40
  '#6EE7B7', // Emerald/40
  '#A5B4FC', // Indigo/40
  '#67E8F9', // Cyan/40
];

const FIGMA_CHART_COLOR_1 = '#1b96ff';
const FIGMA_CHART_COLOR_6 = '#ff538a';
const FIGMA_CHART_COLOR_7 = '#53dcab';

export function primaryColor(): string {
  return FIGMA_CHART_COLOR_1;
}
export function successColor(): string {
  return FIGMA_CHART_COLOR_7;
}
export function dangerColor(): string {
  return FIGMA_CHART_COLOR_6;
}

export function lineColor(): string {
  return getCssVar('--ds-chart-text-primary');
}

function fontFamily(): string {
  return (
    getCssVar('--fontFamiliy') || getCssVar('--fontFamily') || 'system-ui, sans-serif'
  );
}

export const chartTitle = {
  fontSize: 16,
  fontWeight: 600,
  get fontFamily() {
    return fontFamily();
  },
  textAlign: 'left' as const,
  spacing: 4,
};

export const chartSubtitle = {
  fontSize: 14,
  get fontFamily() {
    return fontFamily();
  },
  textAlign: 'left' as const,
  color: '#6b7280',
  spacing: 16,
};

export const BAR_CORNER_RADIUS = 4;

const COMPACT_CURRENCY_FORMATTER = new Intl.NumberFormat('de-DE', {
  notation: 'compact',
  compactDisplay: 'short',
  maximumFractionDigits: 1,
  style: 'currency',
  currency: 'EUR',
});

export function formatCompactCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return COMPACT_CURRENCY_FORMATTER.format(value);
}

export function periodPercent(
  current: number,
  reference: number
): number | undefined {
  if (!reference) return undefined;
  return ((current - reference) / Math.abs(reference)) * 100;
}

const PERIOD_DATE_FORMATTER = new Intl.DateTimeFormat('de-AT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function formatPeriodLabel(
  range: { from: string; to: string } | null | undefined
): string {
  if (!range) return '';
  const from = new Date(range.from);
  const to = new Date(range.to);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return '';
  const sameYear = from.getFullYear() === to.getFullYear();
  const startsJanFirst = from.getMonth() === 0 && from.getDate() === 1;
  if (sameYear && startsJanFirst) return String(from.getFullYear());
  return `${PERIOD_DATE_FORMATTER.format(from)} - ${PERIOD_DATE_FORMATTER.format(to)}`;
}

export const RELOAD_DEBOUNCE_MS = 150;

function chartOverrides(font: string) {
  return {
    common: {
      axes: {
        number: { label: { fontFamily: font } },
        category: { label: { fontFamily: font } },
        time: { label: { fontFamily: font } },
      },
      legend: {
        item: {
          label: { fontFamily: font },

          paddingX: 28,
          marker: { padding: 8 },
        },
      },
    },
  };
}

const themeCache = new Map<string, object>();

function cachedTheme<T extends object>(key: string, build: () => T): T {
  let theme = themeCache.get(key) as T | undefined;
  if (!theme) {
    theme = build();
    themeCache.set(key, theme);
  }
  return theme;
}

function themedCached(kind: string, isDarkMode: boolean, fills: string[]) {
  const font = fontFamily();
  return cachedTheme(`${kind}:${isDarkMode}:${font}:${fills.join('|')}`, () => ({
    baseTheme: isDarkMode ? ('ag-default-dark' as const) : ('ag-default' as const),
    palette: { fills, strokes: fills },
    overrides: chartOverrides(font),
  }));
}

export function getChartTheme(isDarkMode: boolean) {
  return themedCached('default', isDarkMode, CHART_PALETTE);
}

export function getRevenueChartTheme(isDarkMode: boolean) {
  return themedCached('revenue', isDarkMode, [
    CHART_PALETTE[0],
    CHART_PALETTE[1],
    CHART_PALETTE[2],
  ]);
}

export function getMultiColorChartTheme(isDarkMode: boolean) {
  return themedCached('multi', isDarkMode, [...CHART_PALETTE, ...CHART_PALETTE_LIGHT]);
}

export function getSegmentPaletteTheme(isDarkMode: boolean, fills: string[]) {
  return themedCached('segments', isDarkMode, [...fills]);
}
